"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "next/navigation";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { useCodeLang } from "@/components/CodeLangContext";
import {
  getCodeLangFromTrailSearch,
  type CodeLang,
} from "@/lib/code-lang";
import {
  detectPresentCodeLangs,
  publishCodeLangs,
} from "@/lib/code-lang-dom";
import { cn } from "@/lib/utils";

type Lang = CodeLang;
type TogglePlacement = "pending" | "hidden" | "inline" | "local" | "header";

const STORAGE_KEY = "preferred-code-lang";
const DEFAULT_LANGS: Lang[] = ["c", "java", "python"];

const LANG_META: Record<
  Lang,
  {
    label: string;
    headerLabel: string;
    activeClass: string;
    idleClass: string;
    pulseClass: string;
  }
> = {
  c: {
    label: "C언어",
    headerLabel: "C",
    activeClass:
      "border border-blue-500 bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-500 text-white shadow-sm shadow-blue-500/30 dark:from-blue-500 dark:via-blue-600 dark:to-cyan-400 dark:text-white",
    idleClass:
      "border border-blue-200 bg-gradient-to-r from-blue-100 via-sky-50 to-cyan-100 text-blue-800 hover:border-blue-300 hover:from-blue-200 hover:to-cyan-200 dark:border-blue-900 dark:from-blue-950/80 dark:via-blue-950/50 dark:to-cyan-950/60 dark:text-blue-200 dark:hover:border-blue-700",
    pulseClass: "language-toggle-active--c",
  },
  java: {
    label: "Java",
    headerLabel: "Java",
    activeClass:
      "border border-orange-500 bg-gradient-to-r from-orange-700 via-orange-600 to-red-500 text-white shadow-sm shadow-orange-500/30 dark:from-orange-500 dark:via-orange-600 dark:to-red-500 dark:text-white",
    idleClass:
      "border border-orange-200 bg-gradient-to-r from-orange-100 via-amber-50 to-red-100 text-orange-800 hover:border-orange-300 hover:from-orange-200 hover:to-red-200 dark:border-orange-900 dark:from-orange-950/80 dark:via-orange-950/50 dark:to-red-950/60 dark:text-orange-200 dark:hover:border-orange-700",
    pulseClass: "language-toggle-active--java",
  },
  python: {
    label: "Python",
    headerLabel: "Python",
    activeClass:
      "border border-amber-500 bg-gradient-to-r from-blue-700 via-blue-600 to-amber-600 text-white shadow-sm shadow-amber-500/30 dark:from-blue-500 dark:via-blue-600 dark:to-amber-500 dark:text-white",
    idleClass:
      "border border-amber-200 bg-gradient-to-r from-blue-100 via-sky-50 to-amber-100 text-slate-800 hover:border-amber-300 hover:from-blue-200 hover:to-amber-200 dark:border-amber-900 dark:from-blue-950/80 dark:via-slate-950 dark:to-amber-950/60 dark:text-amber-100 dark:hover:border-amber-700",
    pulseClass: "language-toggle-active--python",
  },
};

interface LanguageToggleProps {
  langs?: Lang[];
}

export default function LanguageToggle(props: LanguageToggleProps = {}) {
  return (
    <React.Suspense fallback={null}>
      <LanguageToggleContent {...props} />
    </React.Suspense>
  );
}

function LanguageToggleContent({
  langs = DEFAULT_LANGS,
}: LanguageToggleProps = {}) {
  const { inShell, headerActionSlot } = useArticleShellSlots();
  const { setActiveLang } = useCodeLang();
  const search = useSearchParams().toString();
  const markerRef = React.useRef<HTMLSpanElement | null>(null);
  const langKey = langs.join("|");
  const availableLangs = React.useMemo(
    () => (langs.length > 0 ? langs : DEFAULT_LANGS),
    // MDX의 langs={["c", "java"]} literal 이 렌더마다 새 배열이어도 내용이 같으면 effect 재실행을 막는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [langKey],
  );
  const [togglePlacement, setTogglePlacement] =
    React.useState<TogglePlacement>("pending");
  const [renderLangs, setRenderLangs] = React.useState<Lang[]>(availableLangs);
  const renderLangKey = renderLangs.join("|");

  React.useEffect(() => {
    const presentLangs = detectPresentCodeLangs(
      markerRef.current,
      availableLangs,
    );

    if (presentLangs.length < 2) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRenderLangs(presentLangs);
      setTogglePlacement("hidden");
      return;
    }

    setRenderLangs(presentLangs);

    const marker = markerRef.current;
    const mdxRoot = marker?.parentElement;
    const hasEarlierHeading =
      marker && mdxRoot
        ? Array.from(mdxRoot.querySelectorAll("h2, h3")).some((heading) =>
            Boolean(
              heading.compareDocumentPosition(marker) &
                Node.DOCUMENT_POSITION_FOLLOWING,
            ),
          )
        : false;

    // 본문 중간의 국소 토글은 셸 여부와 관계없이 원래 위치에 고정 없이 둔다.
    // sticky 로 두면 MDX 루트 끝까지 화면을 따라 내려와 본문을 가린다. (JCG-3909)
    if (hasEarlierHeading) {
      setTogglePlacement("local");
      return;
    }

    if (!inShell) {
      setTogglePlacement("inline");
      return;
    }

    if (!headerActionSlot) {
      // 헤더 슬롯 ref 가 잡히기 전에는 원위치 marker 만 두고 판단을 미룬다.
      setTogglePlacement("pending");
      return;
    }

    // 페이지 상단의 전역 토글만 header 로 올린다.
    setTogglePlacement("header");
  }, [availableLangs, inShell, headerActionSlot]);

  // 출석 버튼(AttendanceButton)이 언어별 출석을 렌더하도록, 전환 가능한 언어 목록을
  // html dataset(data-code-langs)에 publish 한다. 토글이 없거나 언어가 1개뿐이면(=단일 언어)
  // 미설정 → 버튼은 단일 출석 모드로 동작. 페이지 이동 시 언마운트 cleanup 으로 staleness 방지. (JCG-2658)
  //
  // 배치가 확정(inline/header)된 뒤에만 publish 한다. 초기 "pending" 은 아직 presentLangs 감지 전이라
  // renderLangs 가 기본값(availableLangs, 예: [c,java,python])일 수 있어, 그대로 publish 하면
  // 단일 언어 페이지에서도 버튼이 잠깐 다국어 모드로 튀며 불필요한 배치 조회·칩 깜빡임을 유발한다.
  React.useEffect(() => {
    const root = document.documentElement;
    const resolved =
      togglePlacement === "inline" ||
      togglePlacement === "local" ||
      togglePlacement === "header";
    return publishCodeLangs(root, resolved ? renderLangs : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderLangKey, togglePlacement]);

  const [lang, setLang] = React.useState<Lang>(availableLangs[0]);

  // 일정 → 과목 지도 → 선호 언어 순으로 복원한다. 일정 진입은 선호 언어를 바꾸지 않는다.
  React.useEffect(() => {
    if (togglePlacement === "pending" || togglePlacement === "hidden") return;

    const studyLang = new URLSearchParams(search).get("studyLang") as Lang | null;
    const trailLang = getCodeLangFromTrailSearch(search);
    const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
    const initial =
      studyLang && renderLangs.includes(studyLang)
        ? studyLang
        : trailLang && renderLangs.includes(trailLang)
          ? trailLang
          : saved && renderLangs.includes(saved)
            ? saved
            : renderLangs[0];
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLang(initial);
    setActiveLang(initial);
    document.documentElement.setAttribute("data-code-lang", initial);

    // HashHighlighter에서 해시 기반 언어 전환 시 버튼 상태 동기화
    const handleLangSwitch = (e: Event) => {
      const next = (e as CustomEvent).detail as Lang;
      if (renderLangs.includes(next)) {
        setLang(next);
        setActiveLang(next);
      }
    };
    window.addEventListener("lang-switch", handleLangSwitch);
    return () => {
      window.removeEventListener("lang-switch", handleLangSwitch);
      setActiveLang(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderLangKey, togglePlacement, setActiveLang, search]);

  const toggle = (next: Lang) => {
    setLang(next);
    setActiveLang(next);
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.setAttribute("data-code-lang", next);
    window.dispatchEvent(new CustomEvent("lang-switch", { detail: next }));
  };

  const getAriaLabel = (l: Lang) =>
    l === "c" ? "C언어로 보기" : `${LANG_META[l].label} 언어로 보기`;

  const renderButtons = (variant: "inline" | "header") =>
    renderLangs.map((l) => (
      <button
        key={l}
        type="button"
        onClick={() => toggle(l)}
        aria-pressed={lang === l}
        aria-label={getAriaLabel(l)}
        className={cn(
          "relative isolate overflow-visible whitespace-nowrap font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
          variant === "header"
            ? "h-7 rounded-md px-2.5 text-[12px]"
            : "rounded-full px-4 py-1.5 text-sm",
          lang === l
            ? [
                "language-toggle-active",
                LANG_META[l].activeClass,
                LANG_META[l].pulseClass,
              ]
            : LANG_META[l].idleClass,
        )}
      >
        {variant === "header" ? LANG_META[l].headerLabel : LANG_META[l].label}
      </button>
    ));

  if (togglePlacement === "hidden") return null;

  const marker = (
    <span
      ref={markerRef}
      hidden
      aria-hidden="true"
      data-language-toggle-marker=""
    />
  );

  if (togglePlacement === "pending") return marker;

  if (togglePlacement === "header") {
    if (!headerActionSlot) return marker;

    return (
      <>
        {marker}
        {createPortal(
          <div
            role="group"
            aria-label="코드 언어 선택"
            className="flex min-w-0 items-center gap-1 overflow-visible rounded-lg border border-border bg-background/90 p-0.5 shadow-sm backdrop-blur-sm"
          >
            {renderButtons("header")}
          </div>,
          headerActionSlot,
        )}
      </>
    );
  }

  return (
    <>
      {marker}
      <div
        role="group"
        aria-label="코드 언어 선택"
        className={
          togglePlacement === "local"
            ? "mb-4 flex flex-wrap gap-1 overflow-visible py-1"
            : "-mx-2 sticky top-[calc(var(--mobile-navbar-offset,var(--language-toggle-navbar-fallback))+var(--language-toggle-sticky-gap))] z-30 mb-4 flex gap-1 border-b border-border bg-background/80 px-2 py-2 backdrop-blur-sm [--language-toggle-navbar-fallback:3rem] [--language-toggle-sticky-gap:2.75rem] sm:[--language-toggle-navbar-fallback:4rem] sm:[--language-toggle-sticky-gap:2rem] md:[--language-toggle-sticky-gap:3.25rem]"
        }
      >
        {renderButtons("inline")}
      </div>
    </>
  );
}
